import React, { useMemo, useEffect, useState, useCallback } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { injectIntl } from 'react-intl';

import { showLoading, hideLoading } from 'app/store/slices/settings';
import { fetchSaveRequest, updateRequestDetails } from 'app/store/slices/requests';
import { updateServiceDetails, fetchServiceDocuments } from 'app/store/slices/services';
import { preparePayload } from 'app/utils/forms/functions';
import _ from 'lodash';
import { setCity, setVehicles } from 'app/store/slices/forms';
import { CircularProgress, makeStyles } from '@material-ui/core';

import { FormServiceContext } from 'app/utils/contexts';
import { fetchActiveInactiveUsers } from 'app/store/slices/user';
import requestEndpoints from 'app/api/services/requestEndpoints';
import { registrationPersonValidationSchema } from 'app/utils/forms/validationsSchemas';
import Person from 'app/components/ServicesForms/Registration/Person';
import { errorHandler } from 'app/utils/errorHandler';
import { setupCompanyPayload } from 'app/utils/forms/services/company';
import { setupVehiclePayload } from 'app/utils/forms/services/vehicles';
import { setupSurveyPayload } from 'app/utils/forms/services/surveys';
import { setupLicensePayload } from 'app/utils/forms/services/licenses';
import { SERVICES_CODES, SERVICES_GROUPS, SERVICES_PROPERTIES } from 'app/config/services';
import { INTERNAL_CONTEXT } from 'app/config/urls';
import { validateLicenseDuplicateRequest, validateLicenseRequest } from 'app/utils/forms/validators/license';

import styles from './styles';
import { setupLinePayload } from 'app/utils/forms/services/lines';
import { validateLineRequest } from '../../../../utils/forms/validators/line';
import { setupChangeEmailPayload } from '../../../../utils/forms/services/change-email';

const useStyles = makeStyles(styles);

function Request(props) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();

    const [errorsList, setErrorsList] = useState({});

    const { intl } = props;
    const [loading, setLoading] = useState(true);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [initialValues, setInitialValues] = useState({});

    const request = useSelector((state) => state.requests.details);
    const vehicles = useSelector((state) => state.forms.vehicles);
    const schedulesStore = useSelector((state) => state.line.schedulesStore);
    const inspectedVehicles = useSelector((state) => state.vehicle.inspectedVehicles);
    const documentsStore = useSelector((state) => state.services.documents);
    const allUsers = useSelector((state) => state.auth.activeInactiveUsers);

    const requestJsonData = useMemo(() => {
        if (request.jsonSolicitacao) return JSON.parse(request.jsonSolicitacao);
        return {};
    }, [request]);

    const [registrationType, setRegistrationType] = useState(() => {
        if (request.jsonSolicitacao) {
            const parsed = JSON.parse(request.jsonSolicitacao);
            if (parsed.tpCadastro) {
                return parsed.tpCadastro;
            } else if (parsed.cdTipoPessoaFisica) {
                return 'person';
            }
        }
        return 'company';
    });

    const validationSchema = useMemo(() => {
        if (registrationType === 'person' && SERVICES_GROUPS.COMPANY.includes(request.cdServico)) {
            return registrationPersonValidationSchema;
        }
        return SERVICES_PROPERTIES[request.cdServico] ? SERVICES_PROPERTIES[request.cdServico].validationSchema : {};
    }, [registrationType, request.cdServico]);

    useEffect(() => {
        (async () => {
            dispatch(showLoading());
            const value = JSON.parse(request.jsonSolicitacao);
            if (SERVICES_GROUPS.COMPANY.includes(request.cdServico)) {
                if (value.enderecoMatriz && value.enderecoMatriz.municipio) {
                    value.enderecoMatriz.uf = value.enderecoMatriz.municipio.uf?.sgUf;
                }
                if (value.enderecoFilial?.municipio?.uf) {
                    value.enderecoFilial.uf = value.enderecoFilial.municipio.uf?.sgUf;
                }
            } else if (SERVICES_GROUPS.VEHICLES.includes(request.cdServico)) {
                if (value.veiculos) {
                    dispatch(setVehicles(value.veiculos));
                }
            } else if (SERVICES_GROUPS.SURVEY.includes(request.cdServico)) {
                if (requestJsonData.vistoriaVeiculos) {
                    const jsonVehicles = requestJsonData.vistoriaVeiculos.map((v) => {
                        return v.veiculo;
                    });
                    dispatch(setVehicles(jsonVehicles));
                }
            } else if (SERVICES_GROUPS.LICENSE.includes(request.cdServico)) {
                if (requestJsonData.licencaVeiculos) {
                    const jsonVehicles = requestJsonData.licencaVeiculos.map((v) => {
                        return v.veiculo;
                    });
                    dispatch(setVehicles(jsonVehicles));
                }
                if (value.nuMeses) value.nuMeses = String(value.nuMeses);
                value.frequencyTest = null;
            }
            setInitialValues(value);
            dispatch(hideLoading());
        })();
    }, [dispatch, request, requestJsonData]);

    if (!request || Object.entries(request).length === 0) {
        history.push(INTERNAL_CONTEXT);
    }

    const canEdit = useMemo(() => {
        return request?.situacao?.boEdicaoPortalInterno;
    }, [request]);

    const [user, setUser] = useState(null);

    useEffect(() => {
        const init = async () => {
            if (request.cdUsuarioResponsavel) {
                if (allUsers && allUsers.length > 0) {
                    const userFound = _.find(allUsers, ['cdUsuario', request.cdUsuarioResponsavel]);
                    setUser(userFound);
                }
            }
        };
        init();
    }, [allUsers, dispatch, request]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const promises = [];
            promises.push(dispatch(setCity(initialValues.municipio)));
            promises.push(dispatch(updateServiceDetails(request.servico)));

            setLoadingDocuments(true);
            const objPromisseDocuments = { cdServico: request.servico?.cdServico };
            if (initialValues.cdTipoPessoaFisica || initialValues.empresa?.cdTipoPessoaFisica) {
                objPromisseDocuments.cdTipoPessoaFisica = parseInt(
                    initialValues.cdTipoPessoaFisica || initialValues.empresa.cdTipoPessoaFisica,
                );
            }
            if (initialValues.cdTipoEmpresa || initialValues.empresa?.cdTipoEmpresa) {
                objPromisseDocuments.cdTipoEmpresa = parseInt(
                    initialValues.cdTipoEmpresa || initialValues.empresa.cdTipoEmpresa,
                );
            }
            promises.push(dispatch(fetchServiceDocuments(objPromisseDocuments)));
            if (allUsers.length === 0) promises.push(dispatch(fetchActiveInactiveUsers()));
            await Promise.all(promises);
            setLoadingDocuments(false);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, request, initialValues]);

    const onSubmit = useCallback(() => {}, []);

    const onSave = useCallback(
        async (values, fromSubmit, isInternalForm, oldData) => {
            if (!canEdit) return;
            let valuesCopy = { ...values };

            if (!valuesCopy.documentos) {
                //only aditional documents
                if (initialValues.documentos && initialValues.documentos.length > 0) {
                    valuesCopy.documentos = initialValues.documentos;
                }
            }

            if (SERVICES_CODES.CHANGE_EMAIL === request.cdServico) {
                valuesCopy = setupChangeEmailPayload({ values: { ...valuesCopy, cdServico: request.cdServico }, intl });
            } else if (SERVICES_GROUPS.COMPANY.includes(request.cdServico)) {
                valuesCopy = setupCompanyPayload({
                    values: valuesCopy,
                    intl,
                    cdServico: request.cdServico,
                    cdSolicitacao: request.cdSolicitacao,
                });
            } else if (SERVICES_GROUPS.VEHICLES.includes(request.cdServico)) {
                valuesCopy = setupVehiclePayload({ values: valuesCopy, requestJsonData, vehicles });
            } else if (SERVICES_GROUPS.SURVEY.includes(request.cdServico)) {
                valuesCopy = setupSurveyPayload({ values: valuesCopy, requestJsonData, vehicles });
            } else if (SERVICES_GROUPS.LICENSE.includes(request.cdServico)) {
                valuesCopy = setupLicensePayload({ values: valuesCopy, requestJsonData, isInternalForm: true });
                let errors;
                if (request.cdServico === SERVICES_CODES.LICENCA_SEGUNDA_VIA) {
                    errors = validateLicenseDuplicateRequest({
                        values: valuesCopy,
                        oldData,
                        intl,
                        isInternalForm: true,
                    });
                } else {
                    errors = validateLicenseRequest({
                        values: valuesCopy,
                        cdServico: request.cdServico,
                        intl,
                        isInternalForm: true,
                    });
                }
                if (Object.keys(errors).length > 0) {
                    setErrorsList(errors);
                    return;
                }
            } else if (
                SERVICES_GROUPS.LINES_SLIC.includes(request.cdServico) ||
                SERVICES_GROUPS.LINES_STRIP.includes(request.cdServico)
            ) {
                valuesCopy = setupLinePayload({
                    values: valuesCopy,
                    inspectedVehicles: inspectedVehicles,
                    cdServico: request.cdServico,
                    linha: requestJsonData.linha,
                    horarios: schedulesStore,
                });

                const errors = validateLineRequest({
                    values: valuesCopy,
                    cdServico: request.cdServico,
                    intl,
                    isInternalForm: true,
                });
                if (Object.keys(errors).length > 0) {
                    setErrorsList(errors);
                    return;
                }
            }

            if (!valuesCopy) return;

            const documentsObject = {
                store: documentsStore,
                files: valuesCopy.documentos,
            };

            const dataToPayload = { ...request };
            if (user && user.cdUsuario !== dataToPayload.cdUsuarioResponsavel) {
                dataToPayload.cdUsuarioResponsavel = user.cdUsuario;
            }

            const preparedPayload = preparePayload({
                values: valuesCopy,
                documents: documentsObject,
                params: dataToPayload,
            });

            try {
                dispatch(showLoading());
                const response = await fetchSaveRequest({
                    isFormData: false,
                    preparedPayload,
                    dispatch,
                    intl,
                    showMessage: true,
                    cdService: request.cdServico,
                    context: '',
                });
                if (response.status === 200) {
                    const responseRequest = await requestEndpoints.findRequest(
                        { cdSolicitacao: dataToPayload.cdSolicitacao },
                        '',
                    );
                    if (responseRequest.status === 200 && responseRequest.data) {
                        await dispatch(updateRequestDetails(responseRequest.data));
                    } else {
                        const updateRequest = { ...dataToPayload };
                        updateRequest.jsonSolicitacao = preparedPayload.payload.jsonSolicitacao;
                        await dispatch(updateRequestDetails(updateRequest));
                    }
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            dispatch(hideLoading());
            return false;
        },
        [
            canEdit,
            dispatch,
            documentsStore,
            initialValues.documentos,
            intl,
            request,
            requestJsonData,
            user,
            vehicles,
            schedulesStore,
            inspectedVehicles,
        ],
    );

    const onCancel = useCallback(() => {
        history.goBack();
    }, [history]);

    const contextProvider = useMemo(() => {
        return {
            isInternalForm: true,
            hideActionBtn: false,
            hideFinalizeBtn: true,
            mainColor: 'secondary',
            loadingDocuments,
            setLoadingDocuments,
            onSave,
            onCancel,
            onSubmit,
            initialValues,
            validationSchema,
            validateOnSave: true,
            canEdit,
            editing: true,
            user,
            registrationType,
            setRegistrationType,
            errorsList,
            setErrorsList,
        };
    }, [
        loadingDocuments,
        onSave,
        onCancel,
        onSubmit,
        initialValues,
        validationSchema,
        canEdit,
        user,
        registrationType,
        errorsList,
        setErrorsList,
    ]);

    const renderRequest = () => {
        if (!request || Object.keys(request).length <= 0 || !request.cdServico) {
            return <></>;
        }
        let Component = SERVICES_PROPERTIES[request.cdServico].component;
        if (SERVICES_GROUPS.COMPANY.includes(request.cdServico)) {
            if (registrationType === 'person') Component = Person;
        }

        return (
            <>
                {loading && (
                    <div className={classes.centerProgress}>
                        <CircularProgress size={20} />
                    </div>
                )}
                {!loading && (
                    <FormServiceContext.Provider value={contextProvider}>
                        <Component {...props} />
                    </FormServiceContext.Provider>
                )}
            </>
        );
    };

    return renderRequest();
}

export default injectIntl(Request);
